import { cn } from '@talentmesh/ui/lib/utils';

import { useMediaQuery } from '../../lib/hooks';
import { type MRT_RowData, type MRT_TableInstance } from '../../types';
import { type BoxProps } from '../../types/mrt-ui-props';
import { parseFromValuesOrFunc } from '../../utils/utils';
import { MRT_GlobalFilterTextInput } from '../inputs/MRT_GlobalFilterTextInput';
import { MRT_Box } from '../mrt/MRT_Box';
import commonClasses from './common.styles.module.css';
import { MRT_ProgressBar } from './MRT_ProgressBar';
import { MRT_TablePagination } from './MRT_TablePagination';
import { MRT_ToolbarAlertBanner } from './MRT_ToolbarAlertBanner';
import { MRT_ToolbarDropZone } from './MRT_ToolbarDropZone';
import { MRT_ToolbarInternalButtons } from './MRT_ToolbarInternalButtons';
import classes from './MRT_TopToolbar.module.css';

interface Props<TData extends MRT_RowData> extends BoxProps {
    table: MRT_TableInstance<TData>;
}

export const MRT_TopToolbar = <TData extends MRT_RowData>({ table, ...rest }: Props<TData>) => {
    const {
        getState,
        options: {
            enableGlobalFilter,
            enablePagination,
            enableToolbarInternalActions,
            mantineTopToolbarProps,
            positionGlobalFilter,
            positionPagination,
            positionToolbarAlertBanner,
            positionToolbarDropZone,
            renderTopToolbarCustomActions,
        },
        refs: { topToolbarRef },
    } = table;

    const { isFullScreen, showGlobalFilter } = getState();

    const isMobile = useMediaQuery('(max-width:720px)');
    const isTablet = useMediaQuery('(max-width:1024px)');

    const toolbarProps = {
        ...parseFromValuesOrFunc(mantineTopToolbarProps, { table }),
        ...rest,
    };

    const stackAlertBanner = isMobile || !!renderTopToolbarCustomActions || (showGlobalFilter && isTablet);

    const globalFilterProps = {
        style: !isTablet
            ? {
                  zIndex: 3,
              }
            : undefined,
        table,
    };

    return (
        <MRT_Box
            {...toolbarProps}
            className={cn(
                commonClasses['common-toolbar-styles'],
                classes['root'],
                isFullScreen && classes['root-fullscreen'],
                toolbarProps?.className,
            )}
            ref={(node: HTMLDivElement | null) => {
                if (node) {
                    topToolbarRef.current = node;
                    const r = toolbarProps?.ref;
                    if (r && typeof r === 'object' && 'current' in r) {
                        (r as { current: HTMLDivElement | null }).current = node;
                    }
                }
            }}
        >
            {positionToolbarAlertBanner === 'top' && (
                <MRT_ToolbarAlertBanner stackAlertBanner={stackAlertBanner} table={table} />
            )}
            {['both', 'top'].includes(positionToolbarDropZone ?? '') && <MRT_ToolbarDropZone table={table} />}
            <div
                className={cn(
                    classes['actions-container'],
                    'flex flex-wrap items-center gap-2',
                    stackAlertBanner && classes['actions-container-stack-alert'],
                )}
            >
                {enableGlobalFilter && positionGlobalFilter === 'left' && (
                    <MRT_GlobalFilterTextInput {...globalFilterProps} />
                )}
                {renderTopToolbarCustomActions?.({ table }) ?? <span />}
                {enableToolbarInternalActions ? (
                    <div className="ml-auto flex flex-wrap-reverse justify-end gap-1">
                        {enableGlobalFilter && positionGlobalFilter === 'right' && (
                            <MRT_GlobalFilterTextInput {...globalFilterProps} />
                        )}
                        <MRT_ToolbarInternalButtons table={table} />
                    </div>
                ) : (
                    enableGlobalFilter &&
                    positionGlobalFilter === 'right' && <MRT_GlobalFilterTextInput {...globalFilterProps} />
                )}
            </div>
            {enablePagination && ['both', 'top'].includes(positionPagination ?? '') && (
                <div className="flex justify-end">
                    <MRT_TablePagination position="top" table={table} />
                </div>
            )}
            <MRT_ProgressBar isTopToolbar table={table} />
        </MRT_Box>
    );
};
